import React, { useState, useEffect } from 'react';
import {
  IconX,
  IconArrowLeft,
  IconArrowRight,
  IconDeviceFloppy,
  IconPlayerSkipForward,
  IconChevronDown,
  IconCurrencyDollar
} from '@tabler/icons-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Spinner } from '../../ui/spinner';
import { Progress } from '../../ui/progress';
import { Alert } from '../../ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../../ui/tooltip';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { cn } from '../../../lib/utils';
import { useReviewState } from './hooks/useReviewState';
import { useScoring } from './hooks/useScoring';
import { useReviewAPI } from './hooks/useReviewAPI';
import { useReviewWebSocket } from './hooks/useReviewWebSocket';
import socketService from '../../../services/socketService';

// Component imports
import ScoringPanel from './components/ScoringPanel';
import FastTurnaroundPanel from './components/FastTurnaroundPanel';
import NotesPanel from './components/NotesPanel';
import ContentPanel from './components/ContentPanel';
import PricingPanel from './components/PricingPanel';
import PerformanceMonitor from './components/PerformanceMonitor';

/**
 * Main Review Modal Component
 * Large modal with two-panel layout for creator reviews
 */
const ReviewModal = ({ open, onClose, creatorIds = [] }) => {
  const [initialized, setInitialized] = useState(false);
  const [mobilePanel, setMobilePanel] = useState('content'); // 'content' | 'scoring' | 'notes' | 'pricing'
  const isMobile = useMediaQuery('(max-width: 639px)');

  // Custom hooks
  const reviewState = useReviewState(creatorIds);
  const scoring = useScoring();
  const api = useReviewAPI();

  // WebSocket for real-time updates
  const { emitReviewUpdate, emitStatusChange } = useReviewWebSocket(
    reviewState.currentCreator?._id,
    (updates) => {
      if (updates) {
        reviewState.updateCurrentReview(updates);
        if (updates.contentQuality || updates.followsInstructions || updates.deliversOnTime) {
          scoring.setAllScores(updates);
        }
      }
    }
  );

  // Initialize session and ensure socket connection when modal opens
  useEffect(() => {
    if (open && creatorIds.length > 0 && !initialized) {
      if (!socketService.isConnected()) {
        socketService.connect();
      }

      reviewState.startSession(creatorIds)
        .then(() => {
          setInitialized(true);
        })
        .catch(error => {
          console.error('Failed to start review session:', error);
          setInitialized(true);
        });
    }

    if (!open && initialized) {
      setInitialized(false);
    }
  }, [open, creatorIds, initialized, reviewState.startSession]);

  // Update scoring from Creator data when we switch creators
  useEffect(() => {
    const creator = reviewState.currentCreator;
    if (!creator) return;

    const reviewScores = creator.reviewScores || {};
    const fastTurnaround = creator.fastTurnaround || null;

    scoring.setAllScores({
      contentQuality: reviewScores.contentQuality || 5,
      followsInstructions: reviewScores.followsInstructions || 5,
      deliversOnTime: reviewScores.deliversOnTime || 5,
      fastTurnaround: fastTurnaround
    });
  }, [reviewState.currentCreator?._id, scoring.setAllScores]);

  // Handle score updates
  const handleScoreUpdate = (field, value) => {
    scoring.updateScore(field, value);
    reviewState.updateCurrentReview({
      [field]: value
    });
  };

  // Handle status update
  const handleStatusUpdate = (status) => {
    reviewState.updateCurrentReview({
      creatorStatus: status
    });
  };

  // Handle notes update
  const handleNotesUpdate = (notesData) => {
    reviewState.updateCurrentReview(notesData);
  };

  // Handle pricing update
  const handlePricingUpdate = (pricingData) => {
    reviewState.updateCurrentReview(pricingData);
  };

  // Handle save
  const handleSave = async () => {
    try {
      await reviewState.saveCurrentReview();
      return true;
    } catch (error) {
      console.error('Error saving review:', error);
      return false;
    }
  };

  // Handle next with auto-save
  const handleNext = async () => {
    await handleSave();
    await reviewState.goToNext();
  };

  // Handle previous with auto-save
  const handlePrevious = async () => {
    await handleSave();
    await reviewState.goToPrevious();
  };

  // Handle modal close
  const handleClose = async () => {
    if (reviewState.isSessionActive) {
      await reviewState.saveCurrentReview();
      reviewState.endSession();
    }
    setInitialized(false);
    onClose();
  };

  // Loading state
  if (!initialized || reviewState.loading) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="max-w-lg">
          <DialogTitle className="sr-only">Loading Review</DialogTitle>
          <DialogDescription className="sr-only">Starting review session</DialogDescription>
          <div className="flex justify-center items-center min-h-[200px] gap-4">
            <Spinner />
            <p className="text-text-primary">Starting review session...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state
  if (reviewState.error) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="max-w-sm">
          <DialogTitle className="sr-only">Review Error</DialogTitle>
          <DialogDescription className="sr-only">An error occurred</DialogDescription>
          <Alert variant="destructive">
            Error starting review session: {reviewState.error}
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // No creators state
  if (!reviewState.currentCreator) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="max-w-sm">
          <DialogTitle className="sr-only">No Creators</DialogTitle>
          <DialogDescription className="sr-only">No creators to review</DialogDescription>
          <Alert>No creators to review.</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // Mobile panel tabs config
  const mobilePanelTabs = [
    { key: 'content', label: 'Content' },
    { key: 'scoring', label: 'Scoring' },
    { key: 'notes', label: 'Notes' },
    { key: 'pricing', label: 'Pricing' },
  ];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className={cn(
        "p-0 gap-0 overflow-hidden flex flex-col [&>button:last-child]:hidden",
        isMobile
          ? "w-full h-full max-w-full max-h-full"
          : "w-[1200px] max-w-[95vw] h-[90vh] max-h-[90vh]"
      )}>
        <DialogTitle className="sr-only">Creator Review: {reviewState.currentCreator?.name}</DialogTitle>
        <DialogDescription className="sr-only">Review creator performance and scoring</DialogDescription>
        {/* Compact Header */}
        <div className="border-b border-glass-border bg-surface-0 px-3 sm:px-4 py-2 sm:py-2.5 flex-shrink-0">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            {/* Left: Name + Progress */}
            <div className="flex items-center gap-2 min-w-0">
              <h6 className="font-semibold text-sm sm:text-base text-text-primary truncate">
                {reviewState.currentCreator.name}
              </h6>
              <Badge className="font-semibold h-5 text-xs flex-shrink-0">
                {reviewState.progress.current}/{reviewState.progress.total}
              </Badge>
              <Progress value={reviewState.progress.percentage} className="h-[3px] w-16 sm:w-24 flex-shrink-0" />
            </div>

            {/* Center: Performance — hidden on mobile */}
            <div className="hidden sm:block">
              <PerformanceMonitor headerMode={true} />
            </div>

            {/* Right: Navigation + Close */}
            <TooltipProvider>
              <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handlePrevious}
                        disabled={!reviewState.hasPrevious}
                        className="h-8 sm:h-7 px-2 text-xs min-w-[44px] sm:min-w-0"
                      >
                        <IconArrowLeft size={14} />
                        <span className="hidden sm:inline ml-1">Prev</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Previous Creator</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="hidden sm:inline">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={reviewState.skipCurrent}
                        disabled={!reviewState.hasNext}
                        className="h-7 px-2 text-xs border-gray-400 text-gray-400 hover:bg-gray-400/10"
                      >
                        <IconPlayerSkipForward size={14} className="mr-1" />
                        Skip
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Skip this creator</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={reviewState.saving}
                        className="h-8 sm:h-7 px-2 text-xs bg-accent hover:bg-accent-hover text-accent-foreground min-w-[44px] sm:min-w-0"
                      >
                        {!reviewState.saving && <IconDeviceFloppy size={14} />}
                        <span className="hidden sm:inline ml-1">{reviewState.saving ? '...' : 'Save'}</span>
                        {isMobile && reviewState.saving && <span>...</span>}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {reviewState.lastSaveTime
                      ? `Last saved: ${new Date(reviewState.lastSaveTime).toLocaleTimeString()}`
                      : 'Save review'}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="sm"
                        onClick={handleNext}
                        disabled={!reviewState.hasNext || reviewState.saving}
                        className="h-8 sm:h-7 px-2 text-xs min-w-[44px] sm:min-w-0"
                      >
                        <span className="hidden sm:inline mr-1">Next</span>
                        <IconArrowRight size={14} />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Save and go to next creator</TooltipContent>
                </Tooltip>

                <div className="w-px h-5 bg-glass-border mx-0.5" />

                <Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8 sm:h-7 sm:w-7 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0">
                  <IconX size={16} />
                </Button>
              </div>
            </TooltipProvider>
          </div>
        </div>

        {/* Mobile panel tabs */}
        {isMobile && (
          <div className="flex border-b border-glass-border bg-surface-0 overflow-x-auto scrollbar-hide flex-shrink-0">
            {mobilePanelTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setMobilePanel(tab.key)}
                className={cn(
                  'flex-1 min-w-0 px-3 py-2.5 text-xs font-medium min-h-[44px]',
                  mobilePanel === tab.key
                    ? 'text-accent border-b-2 border-accent'
                    : 'text-text-secondary'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content area — two-panel on desktop, single panel on mobile */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left Panel - Controls (desktop only) */}
          {!isMobile && (
            <div className="w-1/3 border-r border-glass-border h-full overflow-y-auto bg-surface-0 scrollbar-hide">
              <div className="p-3 flex flex-col gap-2">
                {/* Scoring */}
                <div>
                  <h6 className="font-semibold mb-1.5 text-accent text-[0.75rem] uppercase tracking-wider">
                    Scoring
                  </h6>
                  <ScoringPanel
                    scores={scoring.scores}
                    ratingFields={scoring.ratingFields}
                    onScoreUpdate={handleScoreUpdate}
                  />
                </div>

                <div className="border-t border-glass-border" />

                {/* Fast Turnaround */}
                <div>
                  <h6 className="font-semibold mb-1.5 text-text-secondary text-[0.75rem] uppercase tracking-wider">
                    Fast Turnaround
                  </h6>
                  <FastTurnaroundPanel
                    value={scoring.scores.fastTurnaround}
                    options={scoring.fastTurnaroundOptions}
                    onChange={(value) => handleScoreUpdate('fastTurnaround', value)}
                  />
                </div>

                <div className="border-t border-glass-border" />

                {/* Notes */}
                <div>
                  <h6 className="font-semibold mb-1.5 text-text-secondary text-[0.75rem] uppercase tracking-wider">
                    Notes
                  </h6>
                  <NotesPanel
                    internalNotes={reviewState.currentCreator?.reviewNotes?.internalNotes || ''}
                    feedbackToCreator={reviewState.currentCreator?.reviewNotes?.feedbackToCreator || ''}
                    onNotesUpdate={handleNotesUpdate}
                  />
                </div>

                <div className="border-t border-glass-border" />

                {/* Pricing */}
                <div>
                  <h6 className="font-semibold mb-1.5 text-text-secondary text-[0.75rem] uppercase tracking-wider">
                    Pricing
                  </h6>
                  <PricingPanel
                    defaultPayment={reviewState.currentCreator?.wallet?.defaultPayment || 0}
                    qrtPayment={reviewState.currentCreator?.wallet?.qrtPayment || 0}
                    shortformPayment={reviewState.currentCreator?.wallet?.shortFormPayment || 0}
                    onPricingUpdate={handlePricingUpdate}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Right Panel (desktop) / Full panel (mobile) */}
          <div className={cn("h-full flex flex-col overflow-hidden", isMobile ? "w-full" : "w-2/3")}>
            <div className="flex-1 overflow-y-auto p-3 sm:p-4">
              {/* Mobile: show panel based on tab selection */}
              {isMobile ? (
                <>
                  {mobilePanel === 'content' && (
                    <ContentPanel
                      creator={reviewState.currentCreator}
                      scoringData={{
                        takeoverScore: scoring.takeoverScore,
                        scores: scoring.scores,
                        scoreInfo: scoring.scoreInfo
                      }}
                      onStatusUpdate={handleStatusUpdate}
                    />
                  )}
                  {mobilePanel === 'scoring' && (
                    <div className="space-y-4">
                      <div>
                        <h6 className="font-semibold mb-2 text-accent text-[0.75rem] uppercase tracking-wider">
                          Scoring
                        </h6>
                        <ScoringPanel
                          scores={scoring.scores}
                          ratingFields={scoring.ratingFields}
                          onScoreUpdate={handleScoreUpdate}
                        />
                      </div>
                      <div className="border-t border-glass-border pt-4">
                        <h6 className="font-semibold mb-2 text-text-secondary text-[0.75rem] uppercase tracking-wider">
                          Fast Turnaround
                        </h6>
                        <FastTurnaroundPanel
                          value={scoring.scores.fastTurnaround}
                          options={scoring.fastTurnaroundOptions}
                          onChange={(value) => handleScoreUpdate('fastTurnaround', value)}
                        />
                      </div>
                    </div>
                  )}
                  {mobilePanel === 'notes' && (
                    <div>
                      <h6 className="font-semibold mb-2 text-text-secondary text-[0.75rem] uppercase tracking-wider">
                        Notes
                      </h6>
                      <NotesPanel
                        internalNotes={reviewState.currentCreator?.reviewNotes?.internalNotes || ''}
                        feedbackToCreator={reviewState.currentCreator?.reviewNotes?.feedbackToCreator || ''}
                        onNotesUpdate={handleNotesUpdate}
                      />
                    </div>
                  )}
                  {mobilePanel === 'pricing' && (
                    <div>
                      <h6 className="font-semibold mb-2 text-text-secondary text-[0.75rem] uppercase tracking-wider">
                        Pricing
                      </h6>
                      <PricingPanel
                        defaultPayment={reviewState.currentCreator?.wallet?.defaultPayment || 0}
                        qrtPayment={reviewState.currentCreator?.wallet?.qrtPayment || 0}
                        shortformPayment={reviewState.currentCreator?.wallet?.shortFormPayment || 0}
                        onPricingUpdate={handlePricingUpdate}
                      />
                    </div>
                  )}
                </>
              ) : (
                <ContentPanel
                  creator={reviewState.currentCreator}
                  scoringData={{
                    takeoverScore: scoring.takeoverScore,
                    scores: scoring.scores,
                    scoreInfo: scoring.scoreInfo
                  }}
                  onStatusUpdate={handleStatusUpdate}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
